/**
 * Scenario Data Normalizer
 * 
 * This utility normalizes scenario data structures to ensure consistent display
 * across the application, handling both old and new data formats.
 * 
 * Key issues addressed:
 * - elementaryActions nested inside operationalSteps vs top-level
 * - Missing modesOperatoires field
 * - Different field naming conventions
 */

/**
 * Extracts and flattens elementary actions from any scenario structure
 * @param {Object} scenario - The scenario object
 * @returns {Array} - Flattened array of elementary actions
 */
export const extractElementaryActions = (scenario) => {
    if (!scenario) return [];

    // Case 1: Top-level elementaryActions array (preferred format - AI-generated scenarios)
    if (scenario.elementaryActions && Array.isArray(scenario.elementaryActions) && scenario.elementaryActions.length > 0) {
        console.log('[ScenarioNormalizer] Using top-level elementaryActions');
        return scenario.elementaryActions;
    }

    // Case 2: elementaryActions nested inside each operationalStep
    if (scenario.operationalSteps && Array.isArray(scenario.operationalSteps)) {
        const nestedActions = [];
        let foundNested = false;

        scenario.operationalSteps.forEach(step => {
            if (step.elementaryActions && Array.isArray(step.elementaryActions) && step.elementaryActions.length > 0) {
                foundNested = true;
                nestedActions.push(...step.elementaryActions);
            }
        });

        if (foundNested) {
            console.log(`[ScenarioNormalizer] Extracted ${nestedActions.length} nested elementaryActions from operationalSteps`);
            return nestedActions;
        }
    }

    // Case 3: operationalSteps ARE the elementary actions (manual scenarios)
    // Manual scenarios store actions directly in operationalSteps with elementaryActionId and phase
    if (scenario.operationalSteps && Array.isArray(scenario.operationalSteps) && scenario.operationalSteps.length > 0) {
        const hasActionStructure = scenario.operationalSteps.some(step =>
            (step.elementaryActionId || step.phase) && step.technique
        );

        if (hasActionStructure) {
            console.log('[ScenarioNormalizer] Using operationalSteps as elementary actions (manual scenario format)');
            return scenario.operationalSteps;
        }
    }

    console.log('[ScenarioNormalizer] No elementary actions found');
    return [];
};

/**
 * Generates modes operatoires from elementary actions if not present
 * This is a simplified frontend version - full calculation happens in backend
 * @param {Array} elementaryActions - Array of elementary actions with dependencies
 * @returns {Array} - Array of modes operatoires
 */
export const generateModesOperatoiresFromActions = (elementaryActions) => {
    if (!elementaryActions || elementaryActions.length === 0) {
        return [];
    }

    // Build adjacency list for graph traversal
    const actionMap = new Map();
    elementaryActions.forEach(action => {
        const id = action.elementaryActionId || action.id || `action_${Math.random().toString(36).substr(2, 9)}`;
        actionMap.set(id, {
            ...action,
            elementaryActionId: id
        });
    });

    // Find starting actions (no dependencies or empty dependencies)
    const startActions = elementaryActions.filter(action => {
        const deps = action.dependencies || [];
        return deps.length === 0;
    });

    if (startActions.length === 0) {
        // If no clear start, use first action of CONNAITRE phase
        const connaitreActions = elementaryActions.filter(a => a.phase === 'CONNAITRE');
        if (connaitreActions.length > 0) {
            startActions.push(connaitreActions[0]);
        } else {
            startActions.push(elementaryActions[0]);
        }
    }

    // DFS to find all paths
    const allPaths = [];
    const maxPaths = 10; // Limit to prevent explosion

    const findPaths = (currentId, currentPath, visited) => {
        if (allPaths.length >= maxPaths) return;

        const current = actionMap.get(currentId);
        if (!current) return;

        currentPath.push(currentId);
        visited.add(currentId);

        const nextActions = current.nextActions || [];

        if (nextActions.length === 0) {
            // End of path
            allPaths.push([...currentPath]);
        } else {
            for (const nextId of nextActions) {
                if (!visited.has(nextId)) {
                    findPaths(nextId, currentPath, new Set(visited));
                }
            }
        }

        currentPath.pop();
    };

    // Start DFS from each starting action
    for (const start of startActions) {
        const startId = start.elementaryActionId || start.id;
        if (startId) {
            findPaths(startId, [], new Set());
        }
    }

    // Convert paths to modes operatoires format
    const modesOperatoires = allPaths.slice(0, 6).map((path, index) => {
        // Calculate vraisemblance for this mode
        let minPS = 4;
        let maxDT = 1;

        path.forEach(actionId => {
            const action = actionMap.get(actionId);
            if (action) {
                const ps = parseInt(action.successProbability) || parseInt(action.stepProbability) || 2;
                const dt = parseInt(action.technicalDifficulty) || parseInt(action.difficulty) || 2;
                minPS = Math.min(minPS, ps);
                maxDT = Math.max(maxDT, dt);
            }
        });

        // EBIOS RM matrix lookup
        const VRAISEMBLANCE_MATRIX = {
            1: { 1: 2, 2: 3, 3: 4, 4: 4 },
            2: { 1: 1, 2: 2, 3: 3, 4: 4 },
            3: { 1: 1, 2: 2, 3: 3, 4: 3 },
            4: { 1: 1, 2: 1, 3: 2, 4: 2 }
        };

        const vraisemblance = VRAISEMBLANCE_MATRIX[maxDT]?.[minPS] || 2;

        // Determine description based on phases in path
        const phases = new Set();
        path.forEach(actionId => {
            const action = actionMap.get(actionId);
            if (action?.phase) phases.add(action.phase);
        });

        return {
            id: `mo_${index + 1}`,
            name: `Mode opératoire ${index + 1}`,
            description: `Chemin via ${Array.from(phases).join(' → ')}`,
            path: path,
            vraisemblance: vraisemblance,
            retained: true
        };
    });

    console.log(`[ScenarioNormalizer] Generated ${modesOperatoires.length} modes operatoires`);
    return modesOperatoires;
};

/**
 * Normalizes a scenario to ensure consistent structure
 * @param {Object} scenario - The scenario object to normalize
 * @returns {Object} - Normalized scenario with guaranteed fields
 */
export const normalizeScenario = (scenario) => {
    if (!scenario) return null;

    const normalized = { ...scenario };

    // 1. Extract and normalize elementary actions
    const elementaryActions = extractElementaryActions(scenario);
    if (elementaryActions.length > 0 && !normalized.elementaryActions) {
        normalized.elementaryActions = elementaryActions;
    }

    // 2. Generate modes operatoires if missing
    if (!normalized.modesOperatoires || normalized.modesOperatoires.length === 0) {
        const actions = normalized.elementaryActions || elementaryActions;
        if (actions.length > 0) {
            const generatedModes = generateModesOperatoiresFromActions(actions);
            if (generatedModes.length > 0) {
                normalized.modesOperatoires = generatedModes;
                console.log(`[ScenarioNormalizer] Added ${generatedModes.length} generated modesOperatoires`);
            }
        }
    }

    // 3. Ensure modesOperatoires is always an array
    if (!normalized.modesOperatoires) {
        normalized.modesOperatoires = [];
    }

    return normalized;
};

/**
 * Normalizes an array of scenarios
 * @param {Array} scenarios - Array of scenarios to normalize
 * @returns {Array} - Array of normalized scenarios
 */
export const normalizeScenarios = (scenarios) => {
    if (!Array.isArray(scenarios)) return [];
    return scenarios.map(normalizeScenario).filter(Boolean);
};

/**
 * Checks if a scenario has the old nested structure
 * @param {Object} scenario - The scenario to check
 * @returns {boolean} - True if scenario has nested elementaryActions
 */
export const hasNestedElementaryActions = (scenario) => {
    if (!scenario?.operationalSteps) return false;
    return scenario.operationalSteps.some(step =>
        step.elementaryActions && Array.isArray(step.elementaryActions) && step.elementaryActions.length > 0
    );
};

/**
 * Checks if a scenario needs normalization
 * @param {Object} scenario - The scenario to check
 * @returns {boolean} - True if scenario needs normalization
 */
export const needsNormalization = (scenario) => {
    if (!scenario) return false;

    // Needs normalization if:
    // 1. Has nested elementaryActions but no top-level ones
    const hasNested = hasNestedElementaryActions(scenario);
    const hasTopLevel = scenario.elementaryActions && scenario.elementaryActions.length > 0;
    if (hasNested && !hasTopLevel) return true;

    // 2. Has elementaryActions but no modesOperatoires
    const hasActions = hasTopLevel || hasNested;
    const hasModes = scenario.modesOperatoires && scenario.modesOperatoires.length > 0;
    if (hasActions && !hasModes) return true;

    return false;
};

export default {
    extractElementaryActions,
    generateModesOperatoiresFromActions,
    normalizeScenario,
    normalizeScenarios,
    hasNestedElementaryActions,
    needsNormalization
};
